import { useEffect, useState, useRef, useCallback } from 'react';
import { Box, alpha } from '@mui/material';
import { CRM_PALETTE as P } from '../../theme/brandPalette';
import { watchScroller } from './watchScroller';

/**
 * Enhancer GLOBAL de scrollbars en modales del CRM.
 *
 * Se monta UNA vez en el shell de agente y le pone la barra dorada SIEMPRE visible
 * (arrastrable en touchpad) a cualquier `.MuiDialogContent-root` que desborde — sin
 * tener que tocar cada diálogo. Las scrollbars nativas no son confiables (overlay que
 * se desvanece en Mac/touch), por eso dibujamos la nuestra. (pedido de Evelyn/agentes)
 *
 * Cómo funciona:
 *  - Un MutationObserver detecta cuando se abre/cierra un diálogo (portal en <body>).
 *  - Por cada DialogContent que desborda, ocultamos su barra nativa (clase inyectada)
 *    y montamos un "thumb" fixed posicionado sobre él (rect del elemento).
 *  - Los diálogos que ya traen su propia barra manual se marcan con `__crmManualBar`
 *    y se saltan, para no duplicar.
 */

const HIDE_CLASS = '__crmCustomBar';
const SKIP_CLASS = '__crmManualBar';
const STYLE_ID = 'crm-dialog-scrollbar-style';

function ensureStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent =
    `.${HIDE_CLASS}{scrollbar-width:none!important}` +
    `.${HIDE_CLASS}::-webkit-scrollbar{width:0!important;height:0!important;display:none!important}`;
  document.head.appendChild(s);
}

function DialogThumb({ scrollEl }: { scrollEl: HTMLElement }) {
  const [s, setS] = useState({ top: 0, right: 0, trackH: 0, thumbH: 0, thumbTop: 0, visible: false });
  const drag = useRef<{ startY: number; startScroll: number } | null>(null);

  const recompute = useCallback(() => {
    const el = scrollEl;
    if (!el.isConnected) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight + 1) {
      setS((p) => (p.visible ? { ...p, visible: false } : p));
      return;
    }
    const r = el.getBoundingClientRect();
    const trackH = r.height - 12;
    const thumbH = Math.max(36, (clientHeight / scrollHeight) * trackH);
    const maxTop = trackH - thumbH;
    const thumbTop = ((scrollTop / (scrollHeight - clientHeight)) || 0) * maxTop;
    setS({
      top: r.top + 6,
      right: Math.max(3, window.innerWidth - r.right + 3),
      trackH,
      thumbH,
      thumbTop,
      visible: true,
    });
  }, [scrollEl]);

  useEffect(() => {
    const el = scrollEl;
    el.classList.add(HIDE_CLASS);
    // Ver watchScroller: incluye los recomputes diferidos por la animación de apertura
    // y, además, re-mide cuando el contenido del diálogo cambia después de montar.
    const stop = watchScroller(el, recompute);
    return () => {
      stop();
      el.classList.remove(HIDE_CLASS);
    };
  }, [scrollEl, recompute]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = drag.current;
      if (!d) return;
      const el = scrollEl;
      const r = el.getBoundingClientRect();
      const trackH = r.height - 12;
      const thumbH = Math.max(36, (el.clientHeight / el.scrollHeight) * trackH);
      const maxTop = trackH - thumbH;
      const range = el.scrollHeight - el.clientHeight;
      el.scrollTop = d.startScroll + ((e.clientY - d.startY) / maxTop) * range;
    };
    const onUp = () => {
      if (drag.current) {
        drag.current = null;
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [scrollEl]);

  if (!s.visible) return null;
  return (
    <Box sx={{ position: 'fixed', top: s.top, right: s.right, height: s.trackH, width: 12, zIndex: 1301, pointerEvents: 'none' }}>
      <Box
        onMouseDown={(e) => {
          drag.current = { startY: e.clientY, startScroll: scrollEl.scrollTop };
          document.body.style.userSelect = 'none';
          e.preventDefault();
        }}
        sx={{
          position: 'absolute',
          right: 0,
          width: 9,
          borderRadius: 5,
          top: s.thumbTop,
          height: s.thumbH,
          bgcolor: alpha(P.brand.golden, 0.6),
          '&:hover': { bgcolor: P.brand.golden, width: 11 },
          cursor: 'grab',
          pointerEvents: 'auto',
          transition: 'background 0.15s ease, width 0.12s ease',
        }}
      />
    </Box>
  );
}

export function DialogScrollbars() {
  const [scrollers, setScrollers] = useState<HTMLElement[]>([]);

  useEffect(() => {
    ensureStyle();
    let raf = 0;
    const scan = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const nodes = Array.from(
          document.querySelectorAll<HTMLElement>(`.MuiDialogContent-root:not(.${SKIP_CLASS})`),
        );
        setScrollers((prev) =>
          prev.length === nodes.length && prev.every((n, i) => n === nodes[i]) ? prev : nodes,
        );
      });
    };
    // Los diálogos de MUI montan un portal como hijo directo de <body>; con childList
    // alcanza para detectar apertura/cierre (barato, sin subtree).
    const mo = new MutationObserver(scan);
    mo.observe(document.body, { childList: true });
    scan();
    return () => {
      mo.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      {scrollers.map((el, i) => (
        <DialogThumb key={i} scrollEl={el} />
      ))}
    </>
  );
}

export default DialogScrollbars;
